import React, { useState, useEffect } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  UserCog,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Calendar,
  Mail,
  Shield,
  User
} from "lucide-react";
import useAuth from "../../contexts/AuthContext";
import { UserRole, matchRole } from "../../types/status";

interface ManagedUser {
  id?: string;
  _id?: string;
  name: string;
  email: string;
  role: "applicant" | "employee" | "recruitment_admin" | "student_admin" | "super_admin" | string;
  createdAt?: string;
}

const UserManagement: React.FC = () => {
  const { apiRequest, isSuperAdmin } = useAuth();

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Search & Filter State
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Role modification loading state
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const qParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
      });

      if (search.trim()) qParams.append("q", search.trim());
      if (roleFilter) qParams.append("role", roleFilter);

      const res = await apiRequest(`/auth/users?${qParams.toString()}`);
      if (res?.success) {
        setUsers(res.data);
        setTotalPages(res.totalPages || 1);
        setTotalUsers(res.total || 0);
      } else {
        throw new Error(res?.error || "Failed to load users");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred while fetching users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [currentPage, limit, roleFilter, isSuperAdmin]);

  // Debounced search trigger or handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setUpdatingUserId(userId);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await apiRequest(`/auth/users/${userId}/role`, {
        method: "PUT",
        body: JSON.stringify({ role: newRole }),
      });

      if (res?.success) {
        setSuccessMessage(`Successfully updated user's role to ${newRole}`);
        // Update local state
        setUsers((prev) =>
          prev.map((u) => {
            const uid = u._id || u.id;
            if (uid === userId) {
              return { ...u, role: newRole as any };
            }
            return u;
          })
        );
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(res?.error || "Role update failed");
      }
    } catch (err: any) {
      setError(err.message || "Failed to update user role.");
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-4 shadow-sm">
          <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold text-red-900">Access Denied</h3>
            <p className="text-red-700 mt-1">
              You do not have the required permissions to view this page. This dashboard is strictly restricted to Super Administrators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Helper colors for role badges
  const getRoleBadgeStyle = (role: string) => {
    if (matchRole(role, UserRole.SUPER_ADMIN)) return "bg-rose-50 text-rose-700 border-rose-200";
    if (matchRole(role, UserRole.RECRUITMENT_ADMIN)) return "bg-indigo-50 text-indigo-700 border-indigo-200";
    if (matchRole(role, UserRole.STUDENT_ADMIN)) return "bg-purple-50 text-purple-700 border-purple-200";
    if (matchRole(role, UserRole.EMPLOYEE)) return "bg-blue-50 text-blue-700 border-blue-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 font-serif tracking-tight">
            User Account Directory
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Manage system users, assign operational roles, and audit access credentials.
          </p>
        </div>
        <button
          onClick={fetchUsers}
          className="flex items-center gap-2 self-start md:self-auto px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-medium transition-colors shadow-sm"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh Directory
        </button>
      </div>

      {/* FEEDBACK TOASTS */}
      {successMessage && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4 flex items-center gap-3 shadow-sm animate-fade-in">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          <span className="text-sm font-medium">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* FILTERS & SEARCH CONTAINER */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-6">
        <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row items-center gap-4">
          <div className="relative w-full md:flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or email address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder-slate-400"
            />
          </div>

          <div className="flex w-full md:w-auto items-center gap-3">
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:w-48 bg-slate-50 border border-slate-200 py-2.5 px-3 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
            >
              <option value="">Filter by Role (All)</option>
              <option value={UserRole.APPLICANT}>Applicant</option>
              <option value={UserRole.EMPLOYEE}>Employee</option>
              <option value={UserRole.RECRUITMENT_ADMIN}>Recruitment Admin</option>
              <option value={UserRole.STUDENT_ADMIN}>Student Admission Admin</option>
              <option value={UserRole.SUPER_ADMIN}>Super Admin</option>
            </select>

            <button
              type="submit"
              className="px-5 py-2.5 bg-blue-700 text-white hover:bg-blue-800 rounded-lg text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {/* USERS TABLE */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-slate-400 text-sm font-medium">Loading directory records...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="py-20 text-center">
              <UserCog className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-700">No users found</h3>
              <p className="text-slate-400 text-sm mt-1">
                We couldn't find any users matching your filters or search query.
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">User Details</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Registered Date</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Role Assignment</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((item) => {
                  const uid = item._id || item.id || "";
                  return (
                    <tr key={uid} className="hover:bg-slate-50/50 transition-colors">
                      {/* Name & Email */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-500">
                            {matchRole(item.role, UserRole.SUPER_ADMIN) ? (
                              <Shield className="h-5 w-5 text-amber-500" />
                            ) : (
                              <User className="h-5 w-5" />
                            )}
                          </div>
                          <div>
                            <span className="block font-semibold text-slate-800 text-sm">{item.name}</span>
                            <span className="flex items-center gap-1 text-slate-400 text-xs mt-0.5">
                              <Mail className="h-3 w-3" />
                              {item.email}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Joined Date */}
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric"
                          }) : "N/A"}
                        </span>
                      </td>

                      {/* Current Role Badge */}
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeStyle(item.role)}`}>
                          {matchRole(item.role, UserRole.SUPER_ADMIN) && "Super Admin"}
                          {matchRole(item.role, UserRole.RECRUITMENT_ADMIN) && "Recruitment Admin"}
                          {matchRole(item.role, UserRole.STUDENT_ADMIN) && "Student Admission Admin"}
                          {matchRole(item.role, UserRole.EMPLOYEE) && "Employee"}
                          {matchRole(item.role, UserRole.APPLICANT) && "Applicant"}
                        </span>
                      </td>

                      {/* Role Dropdown Modifier */}
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <select
                            value={item.role?.toUpperCase()}
                            disabled={updatingUserId === uid}
                            onChange={(e) => handleRoleChange(uid, e.target.value)}
                            className="bg-white border border-slate-200 py-1.5 px-2.5 rounded-lg text-xs font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-500/25 transition-all cursor-pointer disabled:opacity-50"
                          >
                            <option value={UserRole.APPLICANT}>Applicant</option>
                            <option value={UserRole.EMPLOYEE}>Employee</option>
                            <option value={UserRole.RECRUITMENT_ADMIN}>Recruitment Admin</option>
                            <option value={UserRole.STUDENT_ADMIN}>Student Admission Admin</option>
                            <option value={UserRole.SUPER_ADMIN}>Super Admin</option>
                          </select>
                          {updatingUserId === uid && (
                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600"></div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* PAGINATION PANEL */}
        {!loading && users.length > 0 && (
          <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Limit selector & count info */}
            <div className="flex items-center gap-4">
              <span className="text-xs text-slate-500 font-medium">
                Showing {Math.min(totalUsers, (currentPage - 1) * limit + 1)} to{" "}
                {Math.min(totalUsers, currentPage * limit)} of {totalUsers} users
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400">Rows:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="bg-white border border-slate-200 rounded py-1 px-1.5 text-xs text-slate-600 font-medium focus:outline-none"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Nav controls */}
            <div className="flex items-center gap-1">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span className="px-2 text-slate-400 text-sm">...</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border
                          ${p === currentPage
                            ? "bg-blue-700 border-blue-700 text-white"
                            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                          }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="p-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
